import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import PartnerCommissions from './Commissions';
import PartnerPayouts from './Payouts';

/*
 * Earnings — one page for the whole money story.
 *
 * Commissions and Payouts were two nav items answering two halves of one question: what did I
 * earn, and what have I taken out. A partner checking on their money had to hold both in their
 * head and know which one to open, and the invoice now sits between them — you raise it against
 * commission in order to withdraw. Three moments, one story, one page.
 *
 * The two views stay in their own files rather than being pasted together. They are large, they
 * own their own fetching and dialogs, and merging the source would have produced one component
 * nobody wants to open. They take `embedded` and skip their own layout chrome and heading; the
 * tab label is the heading now.
 *
 * /commissions and /payouts still work and open the matching tab, because they are in people's
 * history and in links we have already sent.
 */
const TABS = { '/commissions': 'commissions', '/payouts': 'payouts' };

const PartnerEarnings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(TABS[location.pathname] || 'commissions');

  useEffect(() => {
    const forPath = TABS[location.pathname];
    if (forPath && forPath !== tab) setTab(forPath);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the URL honest as the tab changes, so a reload and a shared link land in the same
  // place the partner was looking at.
  const change = (next) => {
    setTab(next);
    navigate(next === 'payouts' ? '/payouts' : '/commissions', { replace: true });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="partner-earnings">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Earnings</h1>
          <p className="text-sm text-[#64748B] mt-1">
            What you have earned, and what you have withdrawn
          </p>
        </div>

        <Tabs value={tab} onValueChange={change} className="w-full">
          <TabsList>
            <TabsTrigger value="commissions" data-testid="tab-commissions">Commissions</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
          </TabsList>

          {/* Mounted only when open. Both fetch on mount and the payouts view holds dialog
              state; rendering the pair on every visit would double the requests and let a
              half-filled payout dialog survive behind a tab nobody is looking at. */}
          <TabsContent value="commissions" className="mt-4">
            {tab === 'commissions' && <PartnerCommissions embedded />}
          </TabsContent>
          <TabsContent value="payouts" className="mt-4">
            {tab === 'payouts' && <PartnerPayouts embedded />}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default PartnerEarnings;
